import React, { useEffect, useRef, useState } from "react";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import InvoicePDF from "./InvoicePDF"; // PDFコンポーネントをインポート
import { Card, CardContent, TextField, Button, Typography, Autocomplete } from "@mui/material";
import { AutocompleteInput, useRedirect } from "react-admin";
import { formatDateToYMDSimple, formatToJapaneseDate } from "./common";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import authProvider from "./authProvider";

const InvoiceForm = () => {
    const { resource, id } = useParams(); 
    const redirect = useRedirect();
    const [invoiceData, setInvoiceData] = useState({
        issueDate: formatDateToYMDSimple(new Date()),
        name: "",
        postCode: "",
        address:"",
        items: [{ date : "", name: "", price: 0, quantity: 0, summary:""}],
        guide:
          `■清掃について\n槽内にたまった汚泥などを抜き取るのが清掃です。\nこれを定期的に実施しないと濁った汚泥が処理水に混じって放出してしまいます。\nこの作業は市町村の許可を受けた浄化槽清掃業者に委託してください。
          \n■清掃作業について\n毎年１回実施してください。ただし全ばっ気方式浄化槽はおおむね6ヵ月に１回以上実施してください。`
    });

  const dateInputRef = useRef(null);

  useEffect(() => {
    if(authProvider){
      authProvider
      .checkAuth()
      .then(() => {})
      .catch(() => {
        redirect("/login")
      });
    }
  }, [authProvider]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_DIR}/${resource}/${id}`, {
          method: "GET",
          headers: new Headers({ "Content-Type": "application/json" }),
          mode: "cors",
          credentials: "include",
        });
        if (!response.ok) {
          throw new Error("Failed to fetch user info");
        }
        const { data } = await response.json();
        if (dateInputRef.current) {
            dateInputRef.current.value = formatDateToYMDSimple(new Date(data.cleaningDate)); // ✅ 直接セット
          }
        const items = [];
        switch(resource){
            case "cleanHistory":
                items.push({ date : data.cleaningDate ? formatDateToYMDSimple(new Date(data.cleaningDate)) : null, name: "浄化槽清掃", price: data.pumpingPay, quantity: data.pumpingAmount, summary:"" });
                items.push({ date : null, name: "消費税(10%)", price: (data.pumpingPay*data.pumpingAmount)*0.1, quantity: 1, summary:"" })
                break;
            default:
                items.push({ date : "", name: "", price: 0, quantity: 0, summary:"" })
                break;
        }

        const guideText = await getGuideText();
        setInvoiceData({
            issueDate: formatDateToYMDSimple(new Date()),
            name: `${data.surname ?? ""}${data.name ?? ""}`,
            postCode: data.postCode ?? "",
            address:`${data.address ?? ""}${data.address2 ?? ""}${data.address3 ?? ""}${data.address4 ?? ""}${data.address5 ?? ""}`,
            items: items,
            guide: guideText ?? `■清掃について\n槽内にたまった汚泥などを抜き取るのが清掃です。\nこれを定期的に実施しないと濁った汚泥が処理水に混じって放出してしまいます。\nこの作業は市町村の許可を受けた浄化槽清掃業者に委託してください。
          \n■清掃作業について\n毎年１回実施してください。ただし全ばっ気方式浄化槽はおおむね6ヵ月に１回以上実施してください。`
        })
      } catch (error) {
        console.error("ユーザー情報の取得に失敗しました:", error);
      } finally {
        // setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const getGuideText = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_DIR}/guideText`, {
        method: "GET",
        headers: new Headers({ "Content-Type": "application/json" }),
        mode: "cors",
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to fetch user info");
      }
      const { data } = await response.json();
      return data;
    } catch (error) {
      console.error("ユーザー情報の取得に失敗しました:", error);
    } finally {
      // setLoading(false);
    }
  };


  // 入力変更ハンドラー
  const handleChange = (e) => {
    console.log(e)
    setInvoiceData({ ...invoiceData, [e.target.name]: e.target.value });
  };

  // 明細の更新
  const handleItemChange = (index, field, value) => {
    const newItems = [...invoiceData.items];
    newItems[index][field] = value;
    setInvoiceData({ ...invoiceData, items: newItems });
  };

  // 明細の追加
  const addItem = () => {
    setInvoiceData({
      ...invoiceData,
      items: [...invoiceData.items, { name: "", price: 0, quantity: 1 }],
    });
  };

  const updateGuideText = async() => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_DIR}/guideText`, {
        method: "POST",
        headers: new Headers({ "Content-Type": "application/json" }),
        mode: "cors",
        credentials: "include",
        body: JSON.stringify({text:invoiceData.guide}),
      });
      if (!response.ok) {
        throw new Error("Failed to fetch user info");
      }
      await response.json();
    } catch (error) {
      console.error("ユーザー情報の取得に失敗しました:", error);
    } finally {
      // setLoading(false);
    }
  }

//   const handleCustomerSelect = (id) => {
//     const customerId = id;
//     const cus = customers.find(c => c.customerId === customerId);
//     setCustomer(cus);
//     console.log(cus)
//     setInvoiceData({
//         issueDate: invoiceData.issueDate,
//         name: `${cus.surname ?? ""}${cus.name ?? ""}`,
//         postCode: cus.postCode  ?? "",
//         address: cus.address,
//         items: invoiceData.items,
//     })
//   };

  

  // 合計金額を計算
  const totalAmount = invoiceData.items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return (
    <Card sx={{ maxWidth: 800, margin: "auto", padding: 3 }}>
    <CardContent>
        {/* <Autocomplete
            options={customers}
            getOptionLabel={(option) => `${option.surname}${option.name}(${option.address})`}
            onChange={(e,selectedOption) => {
                console.log(selectedOption)
                handleCustomerSelect(selectedOption.customerId)
            }}
            renderInput={(params) => <TextField {...params} label="顧客を選択" fullWidth sx={{ mt: 2 }} />}
            freeSolo // ✅ 手入力も可能
        /> */}
        <TextField fullWidth label="発行日" type="date" name="issueDate"  onChange={handleChange} value={invoiceData.issueDate ? dayjs().format(invoiceData.issueDate) : ""} />
        <TextField fullWidth label="請求先名" name="name" value={invoiceData.name} onChange={handleChange} sx={{ mt: 2 }} />
        <TextField fullWidth label="郵便番号" name="postCode" value={invoiceData.postCode} onChange={handleChange} sx={{ mt: 2 }} />
        <TextField fullWidth label="住所" name="address" value={invoiceData.address} onChange={handleChange} sx={{ mt: 2 }}/>
        <TextField fullWidth label="ご案内" name="guide" value={invoiceData.guide} onChange={handleChange} sx={{ mt: 2 }} rows={7} multiline/>

        <Typography variant="h6" sx={{ mt: 3 }}>明細</Typography>
        {invoiceData.items.map((item, index) => (
          <div key={index} style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
            <TextField label="月日" type="date" value={item.date ? dayjs().format(item.date) : ""} onChange={(e) => handleItemChange(index, "date", e.target.value)} />
            <TextField label="品目" value={item.name} onChange={(e) => handleItemChange(index, "name", e.target.value)} />
            <TextField label="数量" type="number" value={item.quantity} onChange={(e) => handleItemChange(index, "quantity", Number(e.target.value))} />
            <TextField label="単価"　 value={item.price} onChange={(e) => handleItemChange(index, "price", e.target.value)} />
            <TextField label="摘要" value={item.summary} onChange={(e) => handleItemChange(index, "summary", e.target.value)} />
          </div>
        ))}
        <Button onClick={addItem} variant="contained" color="primary" sx={{ mt: 2 }}>＋ 明細を追加</Button>
        <div style={{margin:"30px 0"}}>
            <PDFViewer width="100%" height="400px">
            <InvoicePDF invoiceData={{ ...invoiceData, totalAmount }} />
            </PDFViewer>
        </div>

        <PDFDownloadLink
            document={<InvoicePDF invoiceData={{ ...invoiceData, totalAmount }} />}
            fileName={`請求書_${invoiceData.surname ?? ""}${invoiceData.name ?? ""}_${invoiceData.issueDate}.pdf`}
        >
            {({ loading }) => <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={() => updateGuideText()}>請求書をダウンロード</Button>}
        </PDFDownloadLink>


        {/* <PDFDownloadLink document={<InvoicePDF invoiceData={{ ...invoiceData, totalAmount }} />} fileName="invoice.pdf">
          {({ loading }) => (loading ? "ダウンロード中..." : "請求書をダウンロード")}
        </PDFDownloadLink> */}

        {/* <Button onClick={() => redirect("/")} variant="outlined" color="secondary" sx={{ mt: 3 }}>戻る</Button> */}
      </CardContent>
    </Card>
  );
};

export default InvoiceForm;
